import React from "react";

import CloseIcon from "public/icons/close.svg";

import { Theme, useMediaQuery, styled, css } from "@mui/material";

import { Button } from "@/components/form/Button/Button";
import { SvgIcon } from "@/components/ui/svg-icon/SvgIcon";
import { skipForwardProp } from "@/utils/skipForwardProp";
import { NumberWithPostfix, stringMath } from "@/utils/string.utils";
import { clsxm } from "@/utils/tailwindMerge";

import { CommonModalProps } from "../../models";
import {
  LadySliderModalBottomButtons,
  LadySliderModalBottomButtonsPropTypes,
} from "./components/bottom-buttons";
import { useSliderContext } from "./context/Slider.context";

const MARGIN_BETWEEN_PHOTOS = 15;
const THUMBNAIL_PHOTO_WIDTH = "115px";

export type MediaItemRendererPropTypes<T extends unknown[]> = {
  item: T[number];
  index: number;
  isSelected: boolean;
};

type CommonPropTypes<T extends unknown[]> = CommonModalProps & {
  media: T;
  MediaItemRenderer: React.FC<{
    item: T[number];
    index: number;
    isSelected: boolean;
  }>;
};

type PropTypesWithBottomButtons = {
  isBottomButtons: true;
  // TODO: maybe need to move somewhere else. make slider bottom attachments
  bottomButtonsProps: LadySliderModalBottomButtonsPropTypes;
};

type PropTypesWithoutBottomButtons = {
  isBottomButtons?: false;
};

type PropTypes<T extends unknown[]> =
  | CommonPropTypes<T> &
      (PropTypesWithBottomButtons | PropTypesWithoutBottomButtons);

export const SliderModal = <T extends unknown[]>(props: PropTypes<T>) => {
  const { onClose, media, isBottomButtons, MediaItemRenderer } = props;

  const { isZoomed, currentPhotoIndex, setCurrentPhotoIndex, navigation } =
    useSliderContext();

  const upMd = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const upXl = useMediaQuery((theme: Theme) => theme.breakpoints.up("xl"));
  const selectedPhotoWidth = upXl ? "900px" : upMd ? "490px" : "80vw";

  const selectedPhotoHalfWidth = divideByTwo(selectedPhotoWidth);

  const onPlaceholderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!media) return null;

  return (
    <div data-testid="sliderBackground" className="overflow-hidden h-screen">
      <PlaceholderWrapper
        className={clsxm(
          "md:py-6 py-3 select-none w-screen",
          "flex flex-col gap-2",
        )}
        onClick={onPlaceholderClick}
      >
        <div
          aria-label="Close slider"
          role="button"
          className={clsxm(
            "absolute top-4 right-4 z-[1]",
            "md:top-6 md:right-6",
            "cursor-pointer",
          )}
          onClick={onClose}
        >
          <SvgIcon color="white" width={32} height={32}>
            <CloseIcon />
          </SvgIcon>
        </div>
        <MediaListContainer
          currentPhotoIndex={currentPhotoIndex}
          thumbnailPhotoWidth={THUMBNAIL_PHOTO_WIDTH}
          selectedPhotoHalfWidth={selectedPhotoHalfWidth ?? 0}
          isBottomButtons={isBottomButtons}
        >
          {media.map((obj, i) => {
            const isSelected = currentPhotoIndex === i;
            const currentWidth = isSelected
              ? selectedPhotoWidth
              : THUMBNAIL_PHOTO_WIDTH;

            return (
              <PresentationWrapper
                currentWidth={currentWidth}
                key={i}
                onClick={(e) => {
                  if (isSelected) return;
                  setCurrentPhotoIndex(() => i);
                  onPlaceholderClick(e);
                }}
              >
                <div className="flex flex-col h-full justify-center items-center">
                  <MediaWrapperRelated
                    data-testid={`slide${i}` + (isSelected ? " current" : "")}
                  >
                    <MediaItemRenderer
                      item={obj}
                      index={i}
                      isSelected={isSelected}
                    />

                    {!!isSelected && !isZoomed && (
                      <div
                        className={clsxm(
                          "absolute top-1/2 -translate-y-1/2 left-2 right-2",
                          "flex justify-between pointer-events-none min-w-min z-[1]",
                        )}
                      >
                        <StyledButton
                          variant="text"
                          aria-label="Previous slide"
                          onClick={navigation("prev")}
                          className={clsxm(
                            "pointer-events-auto",
                            currentPhotoIndex < 1 && "invisible",
                          )}
                        />
                        <StyledButton
                          variant="text"
                          aria-label="Next slide"
                          onClick={navigation("next")}
                          className={clsxm(
                            "pointer-events-auto",
                            currentPhotoIndex > media.length - 2 && "invisible",
                          )}
                          nextArrow
                        />
                      </div>
                    )}
                  </MediaWrapperRelated>
                </div>
              </PresentationWrapper>
            );
          })}
        </MediaListContainer>
        {isBottomButtons && (
          <LadySliderModalBottomButtons {...props.bottomButtonsProps} />
        )}
      </PlaceholderWrapper>
    </div>
  );
};

const divideByTwo = (str: NumberWithPostfix) => {
  return stringMath(str, (num) => num / 2);
};

const MediaWrapperRelated = styled("div")(() => ({
  position: "relative",
  overflow: "clip",
  borderRadius: "15px",
  maxHeight: "100%",
  maxWidth: "max-content",
}));

type StyledImgProps = {
  currentWidth: string;
};

const PresentationWrapper = styled("div")(
  ({ currentWidth }: StyledImgProps) => ({
    transition: "all 0.5s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: currentWidth,
    paddingInline: `${MARGIN_BETWEEN_PHOTOS}px}`,
  }),
);

type MediaWrapperProps = {
  thumbnailPhotoWidth: string;
  currentPhotoIndex: number;
  selectedPhotoHalfWidth: string;
  isBottomButtons?: boolean;
};

const MediaListContainer = styled("div")(
  ({
    thumbnailPhotoWidth,
    currentPhotoIndex,
    selectedPhotoHalfWidth,
    isBottomButtons,
  }: MediaWrapperProps) => {
    const leftPosition = `calc(50vw - ${
      parseFloat(thumbnailPhotoWidth) * currentPhotoIndex
    }px - ${selectedPhotoHalfWidth})`;

    return {
      display: "flex",
      position: "relative",
      width: "max-content",

      height: !!isBottomButtons ? "calc(100% - 60px)" : "100%",
      overflowX: "clip",
      transition: "all 0.5s ease",
      flexShrink: 0,
      left: leftPosition,
    };
  },
);

const PlaceholderWrapper = styled("div")(
  // using 100% when browser is not supporting dvh units
  // else 100% will be overwritten by 100dvh
  () => css`
    height: 100%;
    height: 100dvh;
  `,
);

type StyledButtonProps = {
  nextArrow?: boolean;
};

const StyledButton = styled(Button, {
  shouldForwardProp: skipForwardProp(["nextArrow"]),
})<StyledButtonProps>(({ theme, nextArrow }) => {
  return {
    backgroundColor: theme.palette.grey?.[900] + " !important",
    opacity: 0.5,
    width: "40px !important",
    height: "40px !important",
    borderRadius: "50% !important",
    "&:after": {
      width: "13px",
      height: "13px",
      borderRight: "2px solid #fff",
      borderBottom: "2px solid #fff",
      content: "''",
      top: "50%",
      left: `${!!nextArrow ? "45%" : "55%"}`,
      margin: "-0.3rem 0 0 -0.3rem",
      transform: `rotate(${!!nextArrow ? -45 : 135}deg)`,
      position: "absolute",
    },
  };
});
